import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import PublicOnboardingContainer from '../components/onboarding/PublicOnboardingContainer';
import { OnboardingStep } from '../components/onboarding/OnboardingNavigation';

const PublicOnboarding: React.FC = () => {
    const { token, organizationSlug } = useParams<{ token?: string; organizationSlug?: string }>();
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState<any | null>(null);
    const [showMobileMenu, setShowMobileMenu] = useState(false);
    const [currentStep, setCurrentStep] = useState<OnboardingStep>('authorize');
    
    // Get the step from URL parameters
    const stepParam = searchParams.get('step');
    const validSteps: OnboardingStep[] = ['authorize', 'contextualize', 'policies', 'rubrics', 'upcoming-dunning'];
    
    // Handle common typos in step parameter
    const normalizedStepParam = stepParam?.toLowerCase().trim();
    const stepMapping: Record<string, OnboardingStep> = {
      'upcoming-dunnings': 'upcoming-dunning',
      'upcoming-dunning': 'upcoming-dunning'
    };
    
    const mappedStep = normalizedStepParam ? stepMapping[normalizedStepParam] || normalizedStepParam : null;
    const initialStep = mappedStep && validSteps.includes(mappedStep as OnboardingStep) ? mappedStep as OnboardingStep : 'authorize';
    const showNavigation = !stepParam; // Hide navigation if a specific step is requested

    // Update current step when initialStep changes (from URL)
    useEffect(() => {
        setCurrentStep(initialStep);
    }, [initialStep]);

    // Handle step changes from navigation
    const handleStepChange = (step: OnboardingStep) => {
        setCurrentStep(step);
    };

  // simple in-memory cache scoped to this page instance
  const cacheRef = useRef<Record<string, any>>({});
  // global cache and in-flight maps to avoid duplicate calls across remounts (e.g., React StrictMode)
  const globalCache = (window as any).__ARIA_PUBLIC_ONBOARDING_CACHE || ((window as any).__ARIA_PUBLIC_ONBOARDING_CACHE = {});
  const inFlight = (window as any).__ARIA_PUBLIC_ONBOARDING_INFLIGHT || ((window as any).__ARIA_PUBLIC_ONBOARDING_INFLIGHT = {});

  const fetchWithDedupe = async (key: string, url: string, mapData?: (d: any) => any) => {
    // Use global cache first
    if (globalCache[key]) {
      return globalCache[key];
    }
    // Await ongoing request if exists
    if (inFlight[key]) {
      return inFlight[key];
    }
    // Start new request
    const p = (async () => {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Failed to load ${key}`);
      const payload = await res.json();
      const data = mapData ? mapData(payload.data || {}) : (payload.data || {});
      globalCache[key] = data;
      return data;
    })();
    inFlight[key] = p;
    try {
      const result = await p;
      return result;
    } finally {
      delete inFlight[key];
    }
  };

  useEffect(() => {
    const fetchStaged = async () => {
      if (!token && !organizationSlug) return;
      setLoading(true);
      const baseUrl = process.env.REACT_APP_BACKEND_BASE_URL || 'http://localhost:8000';

      try {
        // 1) Fast path: basic org + onboarding
        if (organizationSlug) {
          const basicKey = `basic:${organizationSlug}`;
          const basic = cacheRef.current[basicKey] || await fetchWithDedupe(basicKey, `${baseUrl}/api/v1/public/onboarding/org/${organizationSlug}/basic`);
          cacheRef.current[basicKey] = basic;
          setData((prev: any) => ({ ...(prev || {}), ...basic }));
        } else if (token) {
          // fallback for token flow: keep existing single endpoint for now
          const res = await fetch(`${baseUrl}/api/v1/public/onboarding/${token}`);
          if (!res.ok) throw new Error('Invalid or expired link');
          const payload = await res.json();
          setData(payload.data);
          setLoading(false);
          return;
        }

        setLoading(false); // allow first paint after basic

        if (!organizationSlug) return; // nothing else to load for token path above

        // 2) Remaining resources, fetched sequentially and cached
        const stages: Array<{ key: string; url: string; map: (d: any) => Record<string, any> }> = [
          {
            key: `customers:${organizationSlug}`,
            url: `${baseUrl}/api/v1/public/onboarding/org/${organizationSlug}/customers`,
            map: (d) => ({ customers: d.customers || [] }),
          },
          {
            key: `credit-control:${organizationSlug}`,
            url: `${baseUrl}/api/v1/public/onboarding/org/${organizationSlug}/credit-control`,
            map: (d) => ({ 
              customer_categories: d.customer_categories || [],
              ap_archetypes: d.ap_archetypes || [],
              governance_overlays: d.governance_overlays || []
            }),
          },
          {
            key: `templates`,
            url: `${baseUrl}/api/v1/public/onboarding/templates`,
            map: (d) => ({ template_messages: d.template_messages || [] }),
          },
        ];

        for (const stage of stages) {
          try {
            let stageData = cacheRef.current[stage.key] || await fetchWithDedupe(stage.key, stage.url, stage.map);
            cacheRef.current[stage.key] = stageData;
            setData((prev: any) => ({ ...(prev || {}), ...stageData }));
          } catch (e) {
            // ignore individual stage failures; UI can handle partial data
            // optionally set a soft warning state
          }
        }
      } catch (e: any) {
        setError(e.message || 'Failed to load');
        setLoading(false);
      }
    };
    fetchStaged();
  }, [token, organizationSlug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-sm sm:text-base text-gray-600">Loading onboarding...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
        <div className="bg-white rounded-lg shadow-lg p-6 sm:p-8 max-w-md w-full text-center">
          <div className="text-red-500 text-4xl sm:text-6xl mb-4">⚠️</div>
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Link not available</h2>
          <p className="text-sm sm:text-base text-gray-600 mb-4">{error || 'This public link is invalid or has been revoked.'}</p>
        </div>
      </div>
    );
  }

      const { organization, onboarding_status, customers, customer_categories, governance_overlays, ap_archetypes, template_messages } = data;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <h1 className="text-lg sm:text-xl font-semibold text-gray-900 truncate pr-2">{organization?.legal_entity_name}</h1>
            {showNavigation && (
              <button
                onClick={() => setShowMobileMenu(true)}
                className="lg:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              >
                <Menu className="h-6 w-6" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Navigation Overlay */}
      {showMobileMenu && showNavigation && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div 
            className="absolute inset-0 bg-black bg-opacity-50" 
            onClick={() => setShowMobileMenu(false)}
          />
          <div className="absolute right-0 top-0 h-full w-80 max-w-[85vw] bg-white shadow-xl">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold text-gray-900">Navigation</h2>
              <button
                onClick={() => setShowMobileMenu(false)}
                className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="p-4">
              <PublicOnboardingContainer
                organization={organization}
                onboarding_status={onboarding_status}
                customers={customers}
                customer_categories={customer_categories}
                governance_overlays={governance_overlays}
                ap_archetypes={ap_archetypes}
                template_messages={template_messages}
                initialStep={currentStep}
                showNavigation={true}
                isMobileMenu={true}
                onStepChange={(step) => {
                  handleStepChange(step);
                  setShowMobileMenu(false);
                }}
              />
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-8">
        <PublicOnboardingContainer
          organization={organization}
          onboarding_status={onboarding_status}
          customers={customers}
          customer_categories={customer_categories}
          governance_overlays={governance_overlays}
          ap_archetypes={ap_archetypes}
          template_messages={template_messages}
          initialStep={currentStep}
          showNavigation={showNavigation}
          onStepChange={handleStepChange}
        />
      </div>
    </div>
  );
};

export default PublicOnboarding;


